import type { ComponentTemplate } from './types';

/**
 * Compare an incoming library against the current one and classify each
 * incoming template so an import preview can show exactly what will change and
 * let the user resolve conflicts per part (keep mine vs take theirs). Matching
 * is by template `key` first, then by normalised label, then by part number —
 * so a part your teammate created independently still lines up with yours.
 */

function norm(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, ' ') : '';
}

/** Stable content signature — two templates are "the same" if these match. */
export function templateSignature(t: ComponentTemplate): string {
  const pins = (t.pins ?? [])
    .map((p) => `${norm(p.group)}:${norm(p.name)}:${p.side}:${p.kind}:${norm(p.voltage)}`)
    .sort()
    .join('|');
  return JSON.stringify({
    label: norm(t.label),
    category: norm(t.category),
    partNumber: norm(t.partNumber),
    manufacturer: norm(t.manufacturer),
    color: norm(t.color),
    props: Object.entries(t.defaultProperties ?? {})
      .map(([k, v]) => `${norm(k)}=${norm(String(v))}`)
      .sort(),
    mechanical: t.mechanical ?? null,
    pins,
  });
}

function findMatch(
  incoming: ComponentTemplate,
  currentList: ComponentTemplate[],
  usedKeys: Set<string>,
): ComponentTemplate | undefined {
  const avail = currentList.filter((c) => !usedKeys.has(c.key));
  const byKey = avail.find((c) => c.key === incoming.key);
  if (byKey) return byKey;
  const label = norm(incoming.label);
  if (label) {
    const byLabel = avail.find((c) => norm(c.label) === label);
    if (byLabel) return byLabel;
  }
  const pn = norm(incoming.partNumber);
  if (pn) {
    const byPn = avail.find((c) => norm(c.partNumber) === pn);
    if (byPn) return byPn;
  }
  return undefined;
}

export interface LibraryConflict {
  mine: ComponentTemplate;
  theirs: ComponentTemplate;
}

export interface LibraryDiff {
  added: ComponentTemplate[]; // incoming with no counterpart
  conflicts: LibraryConflict[]; // matched but content differs
  unchanged: ComponentTemplate[]; // matched and identical
  onlyMine: ComponentTemplate[]; // current with no incoming counterpart (kept as-is)
}

export function diffLibrary(
  current: ComponentTemplate[],
  incoming: ComponentTemplate[],
): LibraryDiff {
  const usedKeys = new Set<string>();
  const added: ComponentTemplate[] = [];
  const conflicts: LibraryConflict[] = [];
  const unchanged: ComponentTemplate[] = [];

  for (const inc of incoming) {
    const match = findMatch(inc, current, usedKeys);
    if (!match) {
      added.push(inc);
      continue;
    }
    usedKeys.add(match.key);
    if (templateSignature(match) === templateSignature(inc)) unchanged.push(inc);
    else conflicts.push({ mine: match, theirs: inc });
  }

  const onlyMine = current.filter((c) => !usedKeys.has(c.key));
  return { added, conflicts, unchanged, onlyMine };
}

export interface MergeDecisions {
  /** incoming keys (added section) the user chose to include */
  addKeys: Set<string>;
  /** matched current keys where the user chose to take the incoming version */
  takeTheirsForMineKey: Set<string>;
}

/**
 * Produce the merged library list. Everything of MINE is kept; new parts are
 * appended when selected; for a resolved conflict we keep MY key (so my placed
 * instances stay linked) but take the incoming content.
 */
export function applyLibraryMerge(
  current: ComponentTemplate[],
  diff: LibraryDiff,
  decisions: MergeDecisions,
): ComponentTemplate[] {
  const overrideByMineKey = new Map<string, ComponentTemplate>();
  for (const c of diff.conflicts) {
    if (decisions.takeTheirsForMineKey.has(c.mine.key)) {
      overrideByMineKey.set(c.mine.key, { ...c.theirs, key: c.mine.key });
    }
  }
  const result = current.map((t) => overrideByMineKey.get(t.key) ?? t);
  for (const inc of diff.added) {
    if (decisions.addKeys.has(inc.key)) result.push(inc);
  }
  return result;
}
